import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Truck, MapPin, Phone } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Suppliers() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ id: null, fornecedor_id: '', fornecedor_nome: '', fornecedor_cnpj: '', fornecedor_telefone: '', fornecedor_endereco: '', fornecedor_status: 'Ativo' });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores.php`);
      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    // Auto generate a fornecedor_id if empty on POST
    const payload = { ...formData };
    if (!payload.id && !payload.fornecedor_id) {
        payload.fornecedor_id = 'FORN' + Math.floor(Math.random() * 10000);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchSuppliers();
      } else {
        showAlert('Aviso','Erro ao salvar fornecedor');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleEdit = (supplier) => {
    setFormData({ 
      id: supplier.id, 
      fornecedor_id: supplier.fornecedor_id, 
      fornecedor_nome: supplier.fornecedor_nome, 
      fornecedor_cnpj: supplier.fornecedor_cnpj, 
      fornecedor_telefone: supplier.fornecedor_telefone, 
      fornecedor_endereco: supplier.fornecedor_endereco,
      fornecedor_status: supplier.fornecedor_status || 'Ativo'
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este fornecedor?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/fornecedores.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchSuppliers();
      } else {
        showAlert('Aviso','Erro ao excluir fornecedor');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData({ id: null, fornecedor_id: '', fornecedor_nome: '', fornecedor_cnpj: '', fornecedor_telefone: '', fornecedor_endereco: '', fornecedor_status: 'Ativo' });
    setIsModalOpen(true);
  };

  const filteredSuppliers = suppliers.filter(s => 
    (s.fornecedor_nome && s.fornecedor_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.fornecedor_cnpj && s.fornecedor_cnpj.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Fornecedores</h1>
          <p className="page-subtitle">Gerencie as transportadoras, fabricantes e prestadores de serviço</p>
        </div>
        <button className="btn-primary" onClick={openNewModal}>
          <Plus size={18} />
          Novo Fornecedor
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar fornecedor por Nome ou CNPJ..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fornecedor / CNPJ</th>
                <th>Contato & Localização</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>
                    <div className="font-medium" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Truck size={16} className="text-muted" /> {supplier.fornecedor_nome}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{supplier.fornecedor_cnpj}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <Phone size={14} /> {supplier.fornecedor_telefone}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <MapPin size={14} /> {supplier.fornecedor_endereco}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${supplier.fornecedor_status === 'Ativo' ? 'badge-success' : 'badge-danger'}`}>
                      {supplier.fornecedor_status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => handleEdit(supplier)}><Edit2 size={16} /></button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => handleDelete(supplier.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSuppliers.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhum fornecedor encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Fornecedor" : "Novo Fornecedor"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div className="form-group flex-2">
            <label>Razão Social / Nome Fantasia</label>
            <input type="text" name="fornecedor_nome" value={formData.fornecedor_nome} onChange={handleInputChange} placeholder="Ex: Logística Silva" required className="standard-input" />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div className="form-group flex-1">
              <label>CNPJ</label>
              <input type="text" name="fornecedor_cnpj" value={formData.fornecedor_cnpj} onChange={handleInputChange} placeholder="00.000.000/0000-00" required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>Telefone</label>
              <input type="tel" name="fornecedor_telefone" value={formData.fornecedor_telefone} onChange={handleInputChange} placeholder="(00) 0000-0000" required className="standard-input" />
            </div>
          </div>
          <div className="form-group">
            <label>Endereço de Faturamento / Coleta</label>
            <input type="text" name="fornecedor_endereco" value={formData.fornecedor_endereco} onChange={handleInputChange} placeholder="Cidade - UF" required className="standard-input" />
          </div>
          <div className="form-group flex-1">
            <label>Status</label>
            <select name="fornecedor_status" value={formData.fornecedor_status} onChange={handleInputChange} className="standard-input" required>
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-success">Salvar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
